import React, { useState } from 'react';
import { Box, Paper, Typography, TextField, Button, Checkbox, FormControlLabel, InputAdornment, IconButton, Fade } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { useNavigate } from 'react-router-dom';

const riyadhImageUrl = '/images/riyadh.jpg'; // Replace with a Riyadh skyscraper image URL

const Login = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [username, setUsername] = useState('admin');
  const [password, setPassword] = useState('Temp@2025');

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    // Simulate login
    setTimeout(() => {
      setLoading(false);
      if (username !== 'admin' || password !== 'Temp@2025') {
        setError('Invalid username or password.');
      } else {
        localStorage.setItem('isAuthenticated', 'true');
        navigate('/dashboard');
      }
    }, 1200);
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', flexDirection: { xs: 'column', md: 'row' } }}>
      {/* Left: Login Card */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#F9FAFB',
          p: 4,
          flexDirection: 'column',
        }}
      >
        <Fade in timeout={800}>
          <Paper
            elevation={3}
            sx={{
              p: 4,
              minWidth: 340,
              maxWidth: 400,
              width: '100%',
              borderRadius: 4,
              boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.10)',
              borderLeft: '6px solid #6366F1',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: 2,
            }}
          >
            {/* Logo */}
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <TrendingUpIcon sx={{ color: '#6366F1', fontSize: 36 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#6366F1', letterSpacing: 1 }}>
                MJS Holding
              </Typography>
            </Box>
            {/* Welcome message */}
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: '#1E293B', textAlign: 'center' }}>
              Welcome Back
            </Typography>
            <Typography sx={{ color: '#6B7280', mb: 3, textAlign: 'center', fontSize: '1rem' }}>
              Sign in to your Treasury Portal
            </Typography>
            {/* Error message */}
            {error && (
              <Typography sx={{ color: '#F43F5E', mb: 2, fontSize: '0.95rem', textAlign: 'center' }}>{error}</Typography>
            )}
            <Box component="form" onSubmit={handleLogin} sx={{ width: '100%' }}>
              <TextField
                label="Username"
                variant="outlined"
                fullWidth
                margin="normal"
                value={username}
                onChange={e => setUsername(e.target.value)}
                autoComplete="username"
                required
                InputProps={{ sx: { borderRadius: 2 } }}
              />
              <TextField
                label="Password"
                variant="outlined"
                fullWidth
                margin="normal"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                InputProps={{
                  sx: { borderRadius: 2 },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        onClick={() => setShowPassword(v => !v)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1, mb: 2 }}>
                <FormControlLabel
                  control={<Checkbox checked={rememberMe} onChange={e => setRememberMe(e.target.checked)} sx={{ color: '#6366F1' }} />}
                  label={<Typography sx={{ fontSize: '0.95rem', color: '#374151' }}>Remember me</Typography>}
                />
                <Button
                  variant="text"
                  sx={{ color: '#6366F1', fontWeight: 500, textTransform: 'none', fontSize: '0.95rem' }}
                  tabIndex={-1}
                >
                  Forgot password?
                </Button>
              </Box>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                sx={{
                  fontWeight: 700,
                  borderRadius: 2,
                  py: 1.2,
                  fontSize: '1.08rem',
                  boxShadow: 'none',
                  textTransform: 'none',
                  bgcolor: '#6366F1',
                  '&:hover': { bgcolor: '#4F46E5' },
                  mb: 1
                }}
                disabled={loading}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </Box>
          </Paper>
        </Fade>
        <Typography 
          variant="body2" 
          sx={{ 
            mt: 2,
            color: '#9CA3AF',
            textAlign: 'center',
            fontSize: '0.82rem'
          }}
        >
          © 2025 Crafted by Core Nine Team
        </Typography>
      </Box>

      {/* Right: Riyadh City Image with overlay and quote */}
      <Box
        sx={{
          flex: 1,
          display: { xs: 'none', md: 'block' },
          position: 'relative',
          minHeight: '100vh',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url(${riyadhImageUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            zIndex: 1,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: 'rgba(30, 41, 59, 0.55)',
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 6,
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h4" sx={{ color: '#fff', fontWeight: 700, mb: 2, textShadow: '0 2px 8px rgba(0,0,0,0.18)' }}>
              "Empowering Treasury Excellence in Riyadh"
            </Typography>
            <Typography sx={{ color: '#F3F4F6', fontSize: '1.15rem', textShadow: '0 1px 4px rgba(0,0,0,0.12)' }}>
              Secure. Modern. Trusted by MJS Holding.
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Login; 